import { Suspense, lazy } from 'react';
import { Capacitor } from '@capacitor/core';
import { Video } from '../../types/Video';
import { Modal, ModalBody } from '@/components/ui/modal';
import { cn } from '@/lib/utils';

const VideoPlayer = lazy(
  () => import('../../components/ora/Videos/VideoPlayer')
);

interface VideoViewerModalProps {
  open: boolean;
  video: Video | null;
  /** Second to open the player at; undefined plays from the start. */
  startSecond?: number;
  /** The study set the video was opened from (ORA-677), when it came from one. */
  studySetId?: string;
  onClose: () => void;
  onVideoUpdated: (video: Video) => void;
}

const VideoViewerModal: React.FC<VideoViewerModalProps> = ({
  open,
  video,
  startSecond,
  studySetId,
  onClose,
  onVideoUpdated,
}) => {
  // The live/review questions panel renders inside VideoPlayer, gated there by
  // `!isNativePlatform && windowWidth >= 615`. When the active video has questions
  // and that gate can pass, widen the modal so the 22rem panel can sit beside the
  // video on desktop; on narrower viewports the panel wraps beneath the video via
  // VideoPlayer's existing responsive layout, and the modal shrinks to fit the
  // viewport automatically. We match the native check here so the modal doesn't
  // over-stretch on native tablets, where the panel is never shown; the width gate
  // self-corrects on web because the modal is viewport-capped.
  const hasLiveQuestions =
    !Capacitor.isNativePlatform() && (video?.liveQuestions?.length ?? 0) > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      name="video-viewer-modal"
      className={cn(
        'modal--glass',
        hasLiveQuestions && 'modal--video-questions w-[84rem]'
      )}
      size="lg"
    >
      <ModalBody className="modal_content">
        <div className="video-viewer-modal_content">
          {video && (
            <Suspense fallback={null}>
              <VideoPlayer
                video={video}
                getThumbnail={() => video.thumbnailUrl}
                onVideoUpdated={onVideoUpdated}
                manageAIDrawerContext={false}
                initialSeekSeconds={startSecond}
                studySetId={studySetId}
              />
            </Suspense>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default VideoViewerModal;
